import { useQuery } from 'react-query';
import { Auth } from 'aws-amplify';
import { UrlAction } from '../utils/urlHandler';
import {
  QueryGetBuildingsWithinRadiusArgs,
  QueryGetPoisByCodeArgs,
  QueryGetPoisByIdArgs,
  QueryGetPoisByParentIdArgs,
  QueryGetVoucherByCodeArgs,
  TimeRangeInput,
} from '../types/appsync-types';
import { appsyncGraphql, publicAppsyncGraphql } from './appsyncHelper';

getBuildingsWithinRadius.id = 'getBuildingsWithinRadius';

export async function getBuildingsWithinRadius({
  variable = {
    lat: 60.248962,
    lon: 25.011763,
    radius: 1000000,
  },
  poiStatusFilter,
}: {
  variable?: QueryGetBuildingsWithinRadiusArgs;
  poiStatusFilter: TimeRangeInput;
}) {
  const query = /* GraphQL */ `
    query GetBuildingsWithinRadius(
      $lat: Float
      $lon: Float
      $radius: Float
      $input: TimeRangeInput
      $from: AWSDateTime
    ) {
      getBuildingsWithinRadius(lat: $lat, lon: $lon, radius: $radius) {
        id
        lat
        lon
        buildingRef
        title
        timezone
        buildingStatus(input: $input) {
          buildingRef
          from
          to
          bookable
          total
        }
        spaces {
          id
          lat
          lon
          buildingRef
          localRef
          parentRef
          childrenRefs
          layerIndex
          title
          parentRef
          childrenRefs
          resources {
            id
            title
            spaceId
            resourceType
            resourceId
          }
          spaceType
          type
          depositAmountInCents
          currency
          building {
            timezone
          }
          availabilityStatus(from: $from) {
            nextFreeStart
            nextFreeEnd
            status
          }
          billingType
          chargeRates {
            min
            max
            interval
            price
          }
          minimumBookingMinutes
          metadata {
            maxCapacity
            imageUrl
            additionalImageUrls
            additionalInfo
            lockers
            serviceStations
          }
        }
      }
    }
  `;
  try {
    const user = await Auth.currentAuthenticatedUser();
    if (user) {
      return await appsyncGraphql<'getBuildingsWithinRadius'>(query, {
        ...variable,
        from: poiStatusFilter.from,
        input: poiStatusFilter,
      });
    }
    throw new Error('User not found');
  } catch (error) {
    return publicAppsyncGraphql<'getBuildingsWithinRadius'>(query, {
      ...variable,
      from: poiStatusFilter.from,
      input: poiStatusFilter,
    });
  }
}

// TODO: Split this to re-usable functions and fix the horrible naming continue from here
useGetQRPayloadValue.id = 'useGetQRPayloadValue';

export function useGetQRPayloadValue(payload: UrlAction | null) {
  return useQuery(
    [useGetQRPayloadValue.id, ...Object.values(payload ?? {})],
    async () => {
      if (!payload) {
        throw new Error('No payload yet');
      }
      if (payload.type === 'UNLOCK' || payload.type === 'SELECT') {
        const variables: QueryGetPoisByCodeArgs = {
          code: payload.id,
        };
        const query = /* GraphQL */ `
          query GetPoisByCode($code: String!) {
            getPoisByCode(code: $code) {
              id
              lat
              lon
              buildingRef
              localRef
              layerIndex
              title
              resources {
                id
                title
                spaceId
                resourceType
                resourceId
              }
            }
          }
        `;

        const result = await appsyncGraphql<'getPoisByCode' | 'getVoucherByCode'>(query, variables);
        if (result.data?.getPoisByCode) {
          return result;
        }
        throw new Error('Pod not found');
      }
      if (payload.type === 'VOUCHER') {
        const variables: QueryGetVoucherByCodeArgs = {
          code: payload.id,
        };
        const query = /* GraphQL */ `
          query GetVoucherByCode($code: String!) {
            getVoucherByCode(code: $code) {
              voucherNumber
              voucherCode
              enabled
              used
              value
              currency
              sourceName
              creationDate
              expiryDate
            }
          }
        `;

        const result = await appsyncGraphql<'getPoisByCode' | 'getVoucherByCode'>(query, variables);
        if (result.data?.getVoucherByCode) {
          return result;
        }
        throw new Error('Voucher not found');
      }
    },
    { enabled: !!payload },
  );
}

useGetSpaceScheduleInfo.id = 'useGetSpaceScheduleInfo';

export function useGetSpaceScheduleInfo(podId: string, timeInput: TimeRangeInput) {
  return useQuery(
    [useGetSpaceScheduleInfo.id, podId, ...Object.values(timeInput)],
    async () => {
      const variables: QueryGetPoisByIdArgs = {
        id: podId,
      };
      const query = /* GraphQL */ `
        query GetPoisById($id: ID!, $input: TimeRangeInput, $openAt: AWSDateTime) {
          getPoisByID(id: $id) {
            id
            type
            lat
            lon
            buildingRef
            localRef
            layerIndex
            title
            resources {
              id
              title
              spaceId
              resourceType
              resourceId
            }
            building {
              timezone
            }
            minimumBookingMinutes
            reservations(input: $input) {
              id
              userId
              status
              from
              to
            }
            openingHour(at: $openAt) {
              open
              from
              to
            }
            availabilityStatus(from: $openAt) {
              nextFreeStart
              nextFreeEnd
              status
            }
            billingType
          }
        }
      `;

      try {
        const user = await Auth.currentAuthenticatedUser();
        if (user) {
          return await appsyncGraphql<'getPoisByID'>(query, { ...variables, input: timeInput, openAt: timeInput.from });
        }
        throw new Error('User not found');
      } catch (error) {
        return publicAppsyncGraphql<'getPoisByID'>(query, { ...variables, input: timeInput, openAt: timeInput.from });
      }
    },
    {},
  );
}

useGetSpaceInfo.id = 'useGetSpaceInfo';

export function useGetSpaceInfo(podId: string) {
  return useQuery(
    [useGetSpaceInfo.id, podId],
    async () => {
      const variables: QueryGetPoisByIdArgs = {
        id: podId,
      };
      const query = /* GraphQL */ `
        query GetPoisById($id: ID!) {
          getPoisByID(id: $id) {
            id
            type
            buildingRef
            localRef
            childrenRefs
            title
            minimumBookingMinutes
            parentRef
            childrenRefs
            spaceType
            metadata {
              imageUrl
              additionalImageUrls
              additionalInfo
              maxCapacity
              lockers
              serviceStations
              cameraSurveillance
              parkingTerms {
                lang
                url
              }
            }
            currency
            billingType
            chargeRates {
              min
              max
              interval
              price
            }
            building {
              title
              address
              imageUrl
              websiteUrl
            }
          }
        }
      `;

      try {
        const user = await Auth.currentAuthenticatedUser();
        if (user) {
          return await appsyncGraphql<'getPoisByID'>(query, variables);
        }
        throw new Error('User not found');
      } catch (error) {
        return publicAppsyncGraphql<'getPoisByID'>(query, variables);
      }
    },
    {},
  );
}

useGetPoisByParentId.id = 'useGetPoisByParentId';

export function useGetPoisByParentId(parentId: string, timeInput: TimeRangeInput) {
  return useQuery(
    [useGetPoisByParentId.id, parentId, ...Object.values(timeInput)],
    async () => {
      const variables: QueryGetPoisByParentIdArgs = {
        id: parentId,
      };
      const query = /* GraphQL */ `
        query getPoisByParentId($id: ID!, $from: AWSDateTime) {
          getPoisByParentId(id: $id) {
            id
            type
            buildingRef
            localRef
            childrenRefs
            title
            minimumBookingMinutes
            parentRef
            childrenRefs
            spaceType
            metadata {
              imageUrl
              maxCapacity
              lockers
              serviceStations
              cameraSurveillance
              parkingTerms {
                lang
                url
              }
            }
            currency
            billingType
            chargeRates {
              min
              max
              interval
              price
            }
            availabilityStatus(from: $from) {
              nextFreeStart
              nextFreeEnd
              status
            }
            building {
              title
              address
              imageUrl
              websiteUrl
            }
          }
        }
      `;

      try {
        const user = await Auth.currentAuthenticatedUser();
        if (user) {
          return await appsyncGraphql<'getPoisByParentId'>(query, { ...variables, from: timeInput.from });
        }
        throw new Error('User not found');
      } catch (error) {
        return publicAppsyncGraphql<'getPoisByParentId'>(query, { ...variables, from: timeInput.from });
      }
    },
    {},
  );
}

useGetSpaceTerms.id = 'useGetSpaceTerms';

export function useGetSpaceTerms(podId: string | undefined) {
  return useQuery(
    [useGetSpaceTerms.id, podId],
    async () => {
      const variables: QueryGetPoisByIdArgs = {
        id: podId!,
      };
      const query = /* GraphQL */ `
        query GetPoisById($id: ID!) {
          getPoisByID(id: $id) {
            id
            metadata {
              parkingTerms {
                lang
                url
              }
            }
          }
        }
      `;

      try {
        const user = await Auth.currentAuthenticatedUser();
        if (user) {
          return await appsyncGraphql<'getPoisByID'>(query, variables);
        }
        throw new Error('User not found');
      } catch (error) {
        return publicAppsyncGraphql<'getPoisByID'>(query, variables);
      }
    },
    { enabled: !!podId },
  );
}
